import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation } from '@react-navigation/native';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import {
  mockChatData,
  MissionChatData,
  isTreeMission,
  getTreeStepCount,
  getMissionChatScript,
} from '../data/mockChatData';
import { useChatState, buildChatStateKey } from '../context/ChatStateContext';

// CSS grid for 2-column layout on web desktop
const WEB_CHAT_GRID_STYLE: any =
  Platform.OS === 'web'
    ? { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }
    : undefined;

export default function ChatScreen() {
  const navigation = useNavigation<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { width } = useWindowDimensions();
  const isWebDesktop = Platform.OS === 'web' && width >= 768;
  const chatStateStore = useChatState();

  const openMissionChat = (missionId: number) => {
    navigation.navigate('MissionChat', { missionId });
  };

  const renderConversationCard = (item: MissionChatData) => {
    const saved = chatStateStore.getState(buildChatStateKey(item.missionId));
    const totalSteps = isTreeMission(item) && item.conversationTree
      ? getTreeStepCount(item.conversationTree)
      : getMissionChatScript(item).filter(m => m.type && m.type !== 'text').length;
    const answeredCount = saved?.answeredCount ?? 0;
    const progressPct = totalSteps > 0 ? Math.min(100, (answeredCount / totalSteps) * 100) : 0;

    const script = getMissionChatScript(item);
    const lastQuestion = [...script].reverse().find(m => m.type && m.type !== 'text');
    const treeStart = item.conversationTree?.nodes[item.conversationTree.startNodeId];
    const lastMsg = saved?.chatItems.length
      ? (() => {
          for (let i = saved.chatItems.length - 1; i >= 0; i--) {
            const entry = saved.chatItems[i];
            if (entry.kind === 'msg') return entry.data.text;
            if (entry.kind === 'answered') return entry.data.question.text;
          }
          return '';
        })()
      : isTreeMission(item) && treeStart
        ? treeStart.tutorText
        : script[script.length - 1]?.text ?? '';

    const previewText =
      lastMsg.length > 90 ? lastMsg.slice(0, 90) + '\u2026' : lastMsg || (lastQuestion?.text ?? '');

    return (
      <TouchableOpacity
        key={item.missionId}
        style={[
          styles.card,
          isWebDesktop && styles.cardDesktop,
          { backgroundColor: colors.cardBackground },
        ]}
        onPress={() => openMissionChat(item.missionId)}
        activeOpacity={0.75}
      >
        <View style={styles.cardRow}>
          {/* Avatar with unread badge */}
          <View style={styles.avatarWrapper}>
            {item.tutorImage ? (
              <View style={styles.avatarImgWrap}>
                <Image source={item.tutorImage} style={styles.avatarImg} resizeMode="cover" />
              </View>
            ) : (
              <View style={[styles.avatar, { backgroundColor: colors.primary }]}>
                <Text style={[styles.avatarText, { color: colors.badgeText }]}>
                  {item.tutorAvatar}
                </Text>
              </View>
            )}
            {item.unreadCount > 0 && (
              <View style={[styles.unreadBubble, { backgroundColor: colors.badgeBackground }]}>
                <Text style={[styles.unreadText, { color: colors.badgeText }]}>
                  {item.unreadCount}
                </Text>
              </View>
            )}
          </View>

          {/* Info */}
          <View style={styles.cardInfo}>
            {/* Top row: mission title + timestamp */}
            <View style={styles.cardTopRow}>
              <Text
                style={[styles.missionTitle, { color: colors.textPrimary }]}
                numberOfLines={1}
              >
                {item.missionTitle}
              </Text>
              <Text style={[styles.timestamp, { color: colors.textTertiary }]}>
                {item.lastActivity}
              </Text>
            </View>

            {/* Tutor name + role */}
            <Text style={[styles.tutorName, { color: colors.textSecondary }]} numberOfLines={1}>
              {item.tutorName}  ·  {item.tutorRole}
            </Text>

            {/* Progress bar */}
            <View style={styles.progressRow}>
              <View style={[styles.progressTrack, { backgroundColor: colors.border }]}>
                <View
                  style={[
                    styles.progressFill,
                    {
                      backgroundColor: colors.primary,
                      width: `${progressPct}%` as any,
                    },
                  ]}
                />
              </View>
              <Text style={[styles.progressLabel, { color: colors.textTertiary }]}>
                {answeredCount}/{totalSteps} {isTreeMission(item) ? 'echanges' : 'questions'}
              </Text>
            </View>

            {/* Last question preview */}
            <Text
              style={[
                styles.lastMessage,
                {
                  color: item.unreadCount > 0 ? colors.textPrimary : colors.textSecondary,
                  fontWeight: item.unreadCount > 0 ? '600' : '400',
                },
              ]}
              numberOfLines={2}
            >
              {previewText}
            </Text>
          </View>

          {/* Chevron */}
          <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />
        </View>
      </TouchableOpacity>
    );
  };

  const totalUnread = mockChatData.reduce((sum, d) => sum + d.unreadCount, 0);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View
        style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}
      >
        <View style={styles.headerRow}>
          <View>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Messages</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
              {mockChatData.length} conversations de mission
            </Text>
          </View>
          {totalUnread > 0 && (
            <View style={[styles.totalUnreadBadge, { backgroundColor: colors.badgeBackground }]}>
              <Text style={[styles.totalUnreadText, { color: colors.badgeText }]}>
                {totalUnread} non lu{totalUnread > 1 ? 's' : ''}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Conversation list */}
      <ScrollView
        style={[styles.list, { backgroundColor: colors.background }]}
        contentContainerStyle={[
          styles.listContent,
          isWebDesktop && styles.listContentDesktop,
          isWebDesktop && { width: '100%' },
        ]}
        showsVerticalScrollIndicator={false}
      >
        {isWebDesktop && WEB_CHAT_GRID_STYLE ? (
          <View style={WEB_CHAT_GRID_STYLE}>
            {mockChatData.map(renderConversationCard)}
          </View>
        ) : (
          mockChatData.map(renderConversationCard)
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },

  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: { fontSize: 22, fontWeight: '700' },
  headerSubtitle: { fontSize: 13, marginTop: 2 },
  totalUnreadBadge: { borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 },
  totalUnreadText: { fontSize: 12, fontWeight: '700' },

  list: { flex: 1 },
  listContent: { padding: 16 },
  listContentDesktop: {
    maxWidth: 960,
    alignSelf: 'center',
  },

  card: {
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
  cardDesktop: {
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 5,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatarWrapper: { position: 'relative', marginRight: 4 },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: { fontSize: 18, fontWeight: 'bold' },
  avatarImgWrap: { width: 52, height: 52, borderRadius: 26, overflow: 'hidden' },
  avatarImg: { width: 52, height: 52, borderRadius: 26 },
  unreadBubble: {
    position: 'absolute',
    top: -4,
    right: -6,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 5,
  },
  unreadText: { fontSize: 11, fontWeight: '700' },

  cardInfo: { flex: 1, gap: 4 },
  cardTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  missionTitle: { fontSize: 15, fontWeight: '700', flex: 1 },
  timestamp: { fontSize: 11, flexShrink: 0 },
  tutorName: { fontSize: 12 },

  progressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  progressTrack: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressLabel: { fontSize: 11, flexShrink: 0 },

  lastMessage: { fontSize: 13, lineHeight: 18 },
});


